import * as React from 'react'
import * as Snippets from 'snippets'
import { convertSiblingsToJson } from 'helpers'

export class ProductSwatches extends React.PureComponent<ProductSwatches.Props> {
  public isAU = window.Shopify.shop === 'helen-kaminski-au.myshopify.com'

  private formatHexCode = (hex: string): string => {
    // Remove any leading '#' if present
    hex = hex.replace(/^#/, '');

    // Ensure the hex code is 6 characters long
    if (hex.length === 3) {
      hex = hex.split('').map(char => char + char).join('');
    }

    // Add '#' prefix and return
    return '#' + hex.padStart(6, '0').toUpperCase();
  };

  private getColourInfoFromTags = (tags: any[]): { handle: string; mainHex: string; bandHex: string }[] => {
    return tags
      .filter(tag => typeof tag === 'string' && tag.includes('colours:') && !tag.includes(this.props.product.handle))
      .map(tag => {
        const [handle, mainHex, bandHex] = (tag as string).split(':')[1].split('|');
        return {
          handle,
          mainHex: this.formatHexCode(mainHex),
          bandHex: this.formatHexCode(bandHex)
        };
      });
  };

  private getColourInfoFromSiblings = (siblings: any[]): { handle: string; mainHex: string; bandHex: string; }[] => {
    return siblings
      .filter(sibling => sibling.published)
      .map(sibling => {
        const mainHexTag = sibling.tags.find((tag: any) => typeof tag === 'string' && tag.startsWith('main_colour_hex:'));
        const bandHexTag = sibling.tags.find((tag: any) => typeof tag === 'string' && tag.startsWith('band_colour_hex:'));
        return {
          handle: sibling.handle,
          mainHex: mainHexTag ? this.formatHexCode((mainHexTag as string).split(':')[1]) : '',
          bandHex: bandHexTag ? this.formatHexCode((bandHexTag as string).split(':')[1]) : '',
        };
      });
  };

  private getCurrentColourInfo = (product: any): { mainHex: string; bandHex: string } => {
    const { handle, tags } = product;

    // First, check for main_colour_hex and band_colour_hex tags
    const mainHexTag = tags.find((tag: string) => tag.startsWith('main_colour_hex:'));
    const bandHexTag = tags.find((tag: string) => tag.startsWith('band_colour_hex:'));
    
    if (mainHexTag) {
      return {
        mainHex: this.formatHexCode(mainHexTag.split(':')[1]),
        bandHex: bandHexTag ? this.formatHexCode(bandHexTag.split(':')[1]) : ''
      };
    }

    // If main_colour_hex and band_colour_hex are not found, look for the 'colours:' tag
    const currentProductColourTag = tags.find((tag: string) => 
      tag.startsWith('colours:') && tag.includes(handle)
    );

    if (currentProductColourTag) {
      const [, mainHex, bandHex] = currentProductColourTag.split(':')[1].split('|');
      return {
        mainHex: this.formatHexCode(mainHex),
        bandHex: this.formatHexCode(bandHex)
      };
    }

    // If no color information is found, return default values
    return {
      mainHex: '',
      bandHex: ''
    };
  };

  private darkColours = ["#000000", "#434034", "#5e5f61", "#545961", "#02293D", "#000000", "#61605b", "#250003", "#1C5025", "#663300", "#69502d", "#452f13", "#4d6173", "#201E1E", "#543927", "#2C0E03", "#563E3E", "#142B21", "#2e4d46", "#49212c", "#51452F", "#3B2E08", "#2C2D35", "#48351B", "#303546", "#0e131f", "#091C30", "#31355B", "#262423", "#00003A", "#1D1F49", "#06253b", "#323e50", "#452430", "#2e3341", "#494d4d", "#45494f", "#220B02"];
  private lightColours = ["#ffffff", "#F7F5F1", "#f5f4f1", "#faf3e2", "#F2EFDF", "#FCFAF4"].map(color => color.toLowerCase());;

  public render(): JSX.Element {
    const { product, className, collection } = this.props
    const { tags, handle } = product

    // we might have to convert the siblings string to JSON if it is SS data
    let siblings = [];
    if (product.metafields?.dotapparel21?.siblings) {
      siblings = product.metafields?.dotapparel21?.siblings
    } else if (product.mfield_dotapparel21_siblings) {
      siblings = convertSiblingsToJson(product.mfield_dotapparel21_siblings);
    }

    let colours: { handle: string; mainHex: string; bandHex: string }[] = []

    if (siblings.length) {
      // New system
      colours = this.getColourInfoFromSiblings(siblings)
    } else if (Array.isArray(tags) && tags.some(tag => typeof tag === 'string' && tag.includes('colours:'))) {
      // Old system
      colours = this.getColourInfoFromTags(tags).filter(colour => {
        const siblingProduct = product.filteredSiblings.find(sibling => 
          sibling.handle === colour.handle
        );
        // note: US holds only handles
        if (this.isAU) {
          return siblingProduct && siblingProduct.published;
        } else {
          return siblingProduct
        }
      });
    }

    const currentColour = this.getCurrentColourInfo(product)

    const uniqueColours = colours.filter(colour => colour.handle !== handle);

    console.log("colours: ", colours);
    console.log("siblings: ", siblings);
    console.log("product: ", product);

    return (
      <div className={className}>
        <Snippets.Button
          title={product.title}
          name={product.title}
          colour={'blank'}
          className={'mx-0-4 cursor-default'}
        >
          <div className={'m-auto mb-0-8 w-3 h-3 rounded-half border'} style={{ backgroundColor: (currentColour.bandHex || currentColour.mainHex), borderColor: `${this.lightColours.includes((currentColour.bandHex || currentColour.mainHex)?.toLowerCase()) ? '#a9a9a9' : (currentColour.bandHex || currentColour.mainHex)}` }}>
            <div className={'mt-0-4 ml-0-4 absolute w-2 h-2 rounded-half'} style={{ backgroundColor: currentColour.mainHex }}>
              <Snippets.Icon name={'checked'} width={10} height={10} className={`ml-0-5 mt-0-5 ${this.darkColours.includes((currentColour.mainHex)?.toLowerCase()) ? 'text-white' : 'text-black'}`} />
            </div>
          </div>
        </Snippets.Button>
        {uniqueColours.map(({ handle: colorHandle, mainHex, bandHex }) => (
          <Snippets.Button
            key={`${colorHandle}${mainHex}+${bandHex}`}
            title={colorHandle.split('-').slice(1).join(' ')}
            name={colorHandle.split('-').slice(1).join(' ')}
            href={`${collection ? `/collections/${collection.handle}` : ''}/products/${colorHandle}`}
            colour={'blank'}
            className={'mx-0-4'}
          >
            <div className={'m-auto mb-0-8 w-3 h-3 rounded-half border'} style={{ backgroundColor: (bandHex || mainHex), borderColor: `${this.lightColours.includes((bandHex || mainHex)?.toLowerCase()) ? '#e8e4e4' : (bandHex || mainHex)}` }}>
              <div className={'mt-0-4 ml-0-4 absolute w-2 h-2 rounded-half'} style={{ backgroundColor: mainHex }} />
            </div>
          </Snippets.Button>
        ))}
      </div>
    )
  }
}

export namespace ProductSwatches {
  export interface Props {
    product: any;
    className?: string
    collection?: any
  }
}